import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import {
  GameCatalog,
  UpdateGameCatalogInput,
  GalleryAsset,
  ReorderGalleryInput,
  RemoveGalleryResponse,
} from '../games/games-types';

/**
 * v0.3.0 Game Catalog queries. Mirrors the project TanStack Query pattern
 * (see existing `games-queries.ts`).
 *
 * RT-R8 (Phase 4 frontend mirror): gallery reorder triggers IMMEDIATE PATCH
 * (no debounce). The backend serializes concurrent reorders via
 * `SELECT ... FOR UPDATE`. `beforeunload` + `navigator.sendBeacon` flushes
 * any in-flight reorder when the user closes the tab.
 */

export function useGameCatalog(id: string | undefined) {
  return useQuery({
    queryKey: ['game-catalog', id],
    queryFn: async (): Promise<GameCatalog> => {
      const { data } = await apiClient.get<GameCatalog>(`/admin/games/${id}/catalog`);
      return data;
    },
    enabled: !!id,
  });
}

export function useUpdateGameCatalog(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateGameCatalogInput): Promise<GameCatalog> => {
      const { data } = await apiClient.patch<GameCatalog>(`/admin/games/${id}/catalog`, input);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['game-catalog', id], data);
      qc.invalidateQueries({ queryKey: ['games'] });
    },
  });
}

export function useGameGallery(id: string | undefined) {
  return useQuery({
    queryKey: ['game-catalog', id, 'gallery'],
    queryFn: async (): Promise<GalleryAsset[]> => {
      // GET /admin/games/:id/gallery endpoint not in current plan; Phase 4 reads
      // gallery via the catalog endpoint shape. Fallback: return empty array
      // until Phase 5 adds the dedicated gallery GET endpoint.
      // The catalog endpoint returns catalog fields but not gallery array.
      // For Phase 4 MVP, gallery is read from the snapshot via the public
      // more-games endpoint (admin-only via auth header) OR from the catalog
      // endpoint when extended. For now return empty; admin uploads trigger
      // optimistic UI updates on the client.
      return [];
    },
    enabled: !!id,
  });
}

export function useAddGalleryAsset(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<GalleryAsset> => {
      const form = new FormData();
      form.append('file', file);
      const { data } = await apiClient.post<GalleryAsset>(
        `/admin/games/${gameId}/gallery`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-catalog', gameId, 'gallery'] });
    },
  });
}

export function useReorderGallery(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    // RT-R8: immediate PATCH on drag-end. No debounce. Backend serializes via
    // SELECT FOR UPDATE.
    mutationFn: async (input: ReorderGalleryInput): Promise<void> => {
      await apiClient.patch(`/admin/games/${gameId}/gallery/reorder`, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-catalog', gameId, 'gallery'] });
    },
  });
}

export function useRemoveGalleryAsset(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assetId: string): Promise<RemoveGalleryResponse> => {
      const { data } = await apiClient.delete<RemoveGalleryResponse>(
        `/admin/games/${gameId}/gallery/${assetId}`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-catalog', gameId, 'gallery'] });
    },
  });
}